import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { Seo } from "@/components/Seo";

const TYPE_LABEL = { software_review: "Software", product_review: "Product", comparison: "Comparison", buying_guide: "Buying Guide", guide: "Guide" };
const STATUS_STYLE = { published: "bg-stone-900 text-[#CCFF00]", draft: "bg-stone-200 text-stone-700", scheduled: "bg-[#991B1B] text-white" };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const nav = useNavigate();

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/articles").then((r) => setArticles(r.data));
  };
  useEffect(load, []);

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <AdminShell title="Dashboard" actions={
      <Link to="/admin/articles/new" data-testid="new-article-btn" className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 font-mono-pm text-xs tracking-widest uppercase hover:bg-[#991B1B] transition-colors">
        <Plus size={16} /> New Article
      </Link>}>
      <Seo title="Admin Dashboard" noindex />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-t border-l border-stone-900 mb-10">
          {[["Total", stats.total_articles], ["Published", stats.published], ["Drafts", stats.drafts], ["Affiliate Clicks", stats.affiliate_clicks], ["Feedback", stats.feedback]].map(([l, v]) => (
            <div key={l} className="border-r border-b border-stone-900 bg-white p-5">
              <div className="font-mono-pm text-3xl font-black text-stone-900">{v}</div>
              <div className="font-mono-pm text-[10px] tracking-widest uppercase text-stone-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-mono-pm text-xs tracking-widest uppercase text-stone-500 mb-4">All Articles</h2>
      <div className="border border-stone-900 bg-white overflow-x-auto">
        <table className="w-full min-w-[700px]" data-testid="articles-table">
          <thead>
            <tr className="bg-stone-900 text-white font-mono-pm text-[10px] tracking-widest uppercase">
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Score</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.id} className="border-t border-stone-200" data-testid={`admin-article-row-${a.slug}`}>
                <td className="p-3 font-medium text-stone-900">{a.title}</td>
                <td className="p-3 font-mono-pm text-xs text-stone-600">{TYPE_LABEL[a.type]}</td>
                <td className="p-3"><span className={`font-mono-pm text-[10px] tracking-widest uppercase px-2 py-1 ${STATUS_STYLE[a.status]}`}>{a.status}</span></td>
                <td className="p-3 font-mono-pm font-bold text-stone-900">{a.is_scored ? a.overall_score : "—"}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <a href={`${a.type === "comparison" ? "/comparisons" : "/reviews"}/${a.slug}?preview=1`} target="_blank" rel="noopener noreferrer" title="Preview" className="p-2 border border-stone-300 hover:bg-stone-100"><Eye size={14} /></a>
                    <button onClick={() => nav(`/admin/articles/${a.id}`)} title="Edit" data-testid={`edit-${a.slug}`} className="p-2 border border-stone-300 hover:bg-stone-100"><Edit size={14} /></button>
                    <button onClick={() => del(a.id, a.title)} title="Delete" className="p-2 border border-stone-300 hover:bg-[#991B1B] hover:text-white"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {articles.length === 0 && <tr><td colSpan={5} className="p-8 text-center font-mono-pm text-sm text-stone-400">No articles yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}
